import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Breadcrumbs from '../../components/ui/Breadcrumbs';
import QuickActionButton from '../../components/ui/QuickActionButton';
import { NotificationProvider, useNotification } from '../../components/ui/NotificationToast';
import Icon from '../../components/AppIcon';
import { t } from '../../utils/translations';
import { officesService } from '../../services/dataService';

// Import form components
import CategorySelector from './components/CategorySelector';
import MultilingualNameFields from './components/MultilingualNameFields';
import ProfileImageUpload from './components/ProfileImageUpload';
// import BiographyEditor from './components/BiographyEditor';
import ContactInformation from './components/ContactInformation';
// import PositionSpecificFields from './components/PositionSpecificFields';
import FormActions from './components/FormActions';

const OfficeFormContent = () => {
  const { id: routeId } = useParams(); // Get office ID from route params
  const [searchParams] = useSearchParams(); // Get office ID from query params
  const id = routeId || searchParams.get('id'); // Use route param first, then query param
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  // const [currentStep, setCurrentStep] = useState(1); // COMMENTED OUT: No longer using step-based navigation
  
  const { success, error, warning } = useNotification();

  // Form data state
  const [formData, setFormData] = useState({
    category: '',
    name_ar: '',
    name_en: '',
    name_tr: '',
    title_ar: '',
    title_en: '',
    title_tr: '',
    // department_ar: '',
    // department_en: '',
    // department_tr: '',
    // biography_ar: '',
    // biography_en: '',
    // biography_tr: '',
    profileImage: '',
    contacts: [
      { type: 'email', value: '', label: t('officeFormComponents.primaryEmail') }
    ],
    // Position-specific fields
    // termDuration: '',
    // termStartDate: '',
    // termEndDate: '',
    // responsibilities: [],
    // priorityLevel: '',
    // reportingFrequency: '',
    // oversightAreas: '',
    // hasAuditAuthority: false,
    // electionCycle: '',
    // nextElectionDate: '',
    // electoralResponsibilities: [],
    // specialization: '',
    // officeHours: '',
    // officeLocation: '',
    // services: [],
    // isPublicContact: true,
    // isActive: true,
    // displayOrder: ''
  });

  const [errors, setErrors] = useState({});

  // Form steps configuration - COMMENTED OUT: No longer using step-based navigation
  // const formSteps = [
  //   { 
  //     id: 1, 
  //     title: t('officeForm.categoryBasicInfo'), 
  //     icon: 'Building2',
  //     description: t('officeForm.categoryBasicInfoDesc')
  //   },
  //   { 
  //     id: 2, 
  //     title: t('officeForm.profileBiography'), 
  //     icon: 'User',
  //     description: t('officeForm.profileBiographyDesc')
  //   },
  //   { 
  //     id: 3, 
  //     title: t('officeForm.contactInformation'), 
  //     icon: 'Contact',
  //     description: t('officeForm.contactInformationDesc')
  //   },
  //   { 
  //     id: 4, 
  //     title: t('officeForm.positionSettings'), 
  //     icon: 'Settings',
  //     description: t('officeForm.positionSettingsDesc')
  //   },
  //   { 
  //     id: 5, 
  //     title: t('officeForm.reviewPublish'), 
  //     icon: 'CheckCircle',
  //     description: t('officeForm.reviewPublishDesc')
  //   }
  // ];

  // Handle form field changes
  const handleFieldChange = (fieldName, value) => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: value
    }));
    setHasUnsavedChanges(true);
    
    // Clear field error when user starts typing
    if (errors?.[fieldName]) {
      setErrors(prev => ({
        ...prev,
        [fieldName]: ''
      }));
    }
  };

  // Validate form data
  const validateForm = () => {
    const newErrors = {};

    // Category validation - COMMENTED OUT: No longer needed
    // if (!formData?.category) {
    //   newErrors.category = t('officeForm.pleaseSelectCategory');
    // }

    // Name validation - COMMENTED OUT: No longer required
    // if (!formData?.name_ar?.trim()) {
    //   newErrors.name_ar = t('officeForm.arabicNameRequired');
    // }
    // if (!formData?.name_en?.trim()) {
    //   newErrors.name_en = t('officeForm.englishNameRequired');
    // }

    // Title validation - COMMENTED OUT: No longer required
    // if (!formData?.title_ar?.trim()) {
    //   newErrors.title_ar = t('officeForm.arabicTitleRequired');
    // }
    // if (!formData?.title_en?.trim()) {
    //   newErrors.title_en = t('officeForm.englishTitleRequired');
    // }

    // Biography validation - COMMENTED OUT: No longer needed
    // if (!formData?.biography_ar?.trim()) {
    //   newErrors.biography_ar = t('officeForm.arabicBiographyRequired');
    // }

    // Contact validation - COMMENTED OUT: No longer required
    // const validContacts = formData?.contacts?.filter(contact => contact?.value?.trim()) || [];
    // if (validContacts?.length === 0) {
    //   newErrors.contacts = t('officeForm.atLeastOneContact');
    // }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  // Handle form submission
  const handleSave = async (action = 'save') => {
    setIsLoading(true);

    try {
      // Extract email and phone from contacts array
      const emailContact = formData?.contacts?.find(c => c?.type === 'email' && c?.value?.trim());
      const phoneContact = formData?.contacts?.find(c => (c?.type === 'phone' || c?.type === 'mobile') && c?.value?.trim());

      // Map form data to database schema
      const officeData = {
        nameAr: formData?.name_ar || '',
        nameEn: formData?.name_en || '',
        nameTr: formData?.name_tr || '',
        headNameAr: formData?.name_ar || '', // Person's name (using name field)
        headNameEn: formData?.name_en || '',
        headNameTr: formData?.name_tr || '',
        positionAr: formData?.title_ar || '',
        positionEn: formData?.title_en || '',
        positionTr: formData?.title_tr || '',
        email: emailContact?.value || '',
        phone: phoneContact?.value || '',
        imageUrl: formData?.profileImage || '',
        category: formData?.category || 'offices', // Use selected category or default to 'offices'
        orderIndex: 0
      };

      let savedOffice;
      
      if (isEditMode && id) {
        // Update existing office
        savedOffice = await officesService.update(id, officeData);
        success(t('officeForm.officeUpdated') || 'تم تحديث المكتب بنجاح');
      } else {
        // Create new office
        savedOffice = await officesService.create(officeData);
        success(t('officeForm.officeCreated') || 'تم إنشاء المكتب بنجاح');
      }

      setHasUnsavedChanges(false);
      
      if (action === 'publish') {
        success(t('officeForm.officePositionPublished'), {
          description: t('officeForm.positionVisibleOnWebsite'),
          action: {
            label: t('officeForm.viewPosition'),
            onClick: () => window.location.href = '/offices-management'
          }
        });
      } else {
        success(t('officeForm.draftSaved'));
      }

      // Redirect to offices management after a short delay
      setTimeout(() => {
        window.location.href = '/offices-management';
      }, 1500);

    } catch (err) {
      console.error('Error saving office:', err);
      error(t('officeForm.failedToSave') || 'فشل في حفظ البيانات');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle preview
  const handlePreview = (data) => {
    // Open preview in new tab/modal
    console.log('Preview data:', data);
    success(t('officeForm.previewOpened'));
  };

  // Handle cancel
  const handleCancel = () => {
    window.location.href = '/offices-management';
  };

  // Handle image upload
  const handleImageChange = (imageUrl, file) => {
    handleFieldChange('profileImage', imageUrl);
    success(t('officeForm.profileImageUploaded'));
  };

  // Handle image removal
  const handleImageRemove = () => {
    handleFieldChange('profileImage', '');
    success(t('officeForm.profileImageRemoved'));
  };

  // Load office data if editing
  useEffect(() => {
    const loadOfficeData = async () => {
      if (id) {
        setIsEditMode(true);
        setIsLoading(true);
        try {
          const office = await officesService.getById(id);
          
          // Map database data to form data structure
          setFormData({
            category: office?.category || '',
            name_ar: office?.headNameAr || office?.nameAr || '',
            name_en: office?.headNameEn || office?.nameEn || '',
            name_tr: office?.headNameTr || office?.nameTr || '',
            title_ar: office?.positionAr || '',
            title_en: office?.positionEn || '',
            title_tr: office?.positionTr || '',
            profileImage: office?.imageUrl || '',
            contacts: (() => {
              const contacts = [
                ...(office?.email ? [{ type: 'email', value: office.email, label: t('officeFormComponents.primaryEmail') }] : []),
                ...(office?.phone ? [{ type: 'phone', value: office.phone, label: t('officeFormComponents.phoneNumber') }] : [])
              ];
              return contacts.length > 0 ? contacts : [{ type: 'email', value: '', label: t('officeFormComponents.primaryEmail') }];
            })()
          });
        } catch (err) {
          console.error('Error loading office:', err);
          error(t('officeForm.failedToLoad') || 'فشل في تحميل البيانات');
        } finally {
          setIsLoading(false);
        }
      }
    };

    loadOfficeData();
  }, [id]);

  // Auto-save functionality
  useEffect(() => {
    if (hasUnsavedChanges) {
      const autoSaveTimer = setTimeout(() => {
        handleSave('draft');
      }, 30000); // Auto-save after 30 seconds

      return () => clearTimeout(autoSaveTimer);
    }
  }, [formData, hasUnsavedChanges]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e?.ctrlKey || e?.metaKey) {
        switch (e?.key) {
          case 's':
            e?.preventDefault();
            handleSave('draft');
            break;
          case 'p':
            e?.preventDefault();
            handlePreview(formData);
            break;
          case 'Enter':
            e?.preventDefault();
            handleSave('publish');
            break;
        }
      } else if (e?.key === 'Escape') {
        handleCancel();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [formData]);

  // Step navigation - COMMENTED OUT: No longer using step-based navigation
  // const goToStep = (stepNumber) => {
  //   setCurrentStep(stepNumber);
  // };

  // const nextStep = () => {
  //   if (currentStep < formSteps?.length) {
  //     setCurrentStep(currentStep + 1);
  //   }
  // };

  // const prevStep = () => {
  //   if (currentStep > 1) {
  //     setCurrentStep(currentStep - 1);
  //   }
  // };

  return (
    <div className="min-h-screen bg-background">
      <Header 
        onMenuToggle={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        isMenuOpen={isMobileMenuOpen}
      />
      <Sidebar 
        isCollapsed={isSidebarCollapsed}
        onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
      />
      <main className={`transition-all duration-300 ${
        isSidebarCollapsed ? 'lg:ml-16' : 'lg:ml-60'
      } pt-16`}>
        <div className="p-6 max-w-6xl mx-auto">
          {/* Header Section */}
          <div className="mb-8">
            <Breadcrumbs />
            
            <div className="flex items-center justify-between mt-4">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-primary rounded-lg flex items-center justify-center">
                  <Icon name="Plus" size={24} color="white" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-foreground font-arabic">
                    {t('officeForm.title')}
                  </h1>
                  <p className="text-muted-foreground">
                    {t('officeForm.description')}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Progress Steps - COMMENTED OUT: No longer using step-based navigation */}
          {/* <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-foreground">
                {t('officeForm.step')} {currentStep} {t('officeForm.of')} {formSteps?.length}
              </h2>
              <div className="text-sm text-muted-foreground">
                {Math.round((currentStep / formSteps?.length) * 100)}% {t('officeForm.complete')}
              </div>
            </div>
            
            <div className="flex space-x-2 mb-6">
              {formSteps?.map((step) => (
                <div
                  key={step?.id}
                  className={`flex-1 h-2 rounded-full transition-colors ${
                    step?.id <= currentStep ? 'bg-primary' : 'bg-muted'
                  }`}
                />
              ))}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              {formSteps?.map((step) => (
                <button
                  key={step?.id}
                  onClick={() => goToStep(step?.id)}
                  className={`p-4 rounded-lg border text-left transition-all ${
                    step?.id === currentStep
                      ? 'border-primary bg-primary/5 text-primary'
                      : step?.id < currentStep
                      ? 'border-success bg-success/5 text-success hover:bg-success/10' :'border-border bg-card text-muted-foreground hover:bg-muted'
                  }`}
                >
                  <div className="flex items-center space-x-3 mb-2">
                    <Icon 
                      name={step?.icon} 
                      size={18} 
                      className={
                        step?.id === currentStep
                          ? 'text-primary'
                          : step?.id < currentStep
                          ? 'text-success' :'text-muted-foreground'
                      }
                    />
                    <span className="font-medium text-sm">{step?.title}</span>
                  </div>
                  <p className="text-xs opacity-75">{step?.description}</p>
                </button>
              ))}
            </div>
          </div> */}

          {/* Form Content */}
          <div className="grid grid-cols-1 gap-8">
            {/* Main Form */}
            <div className="space-y-8">
              {/* Simplified Form: Only Name, Position, Image, and Contact Information */}
              
              {/* Category Selector */}
              <CategorySelector
                selectedCategory={formData?.category}
                onCategoryChange={(value) => handleFieldChange('category', value)}
                error={errors?.category}
                disabled={isLoading}
              />
              
              {/* Name and Position Fields */}
              <MultilingualNameFields
                formData={formData}
                onFieldChange={handleFieldChange}
                errors={errors}
                disabled={isLoading}
              />

              {/* Profile Image */}
              <ProfileImageUpload
                currentImage={formData?.profileImage}
                onImageChange={handleImageChange}
                onImageRemove={handleImageRemove}
                disabled={isLoading}
                error={errors?.profileImage}
              />

              {/* Contact Information */}
              <ContactInformation
                formData={formData}
                onFieldChange={handleFieldChange}
                errors={errors}
                disabled={isLoading}
              />

              {/* Form Actions */}
              <div className="pt-6 border-t border-border">
                <FormActions
                  onSave={handleSave}
                  onPreview={handlePreview}
                  onCancel={handleCancel}
                  isLoading={isLoading}
                  hasUnsavedChanges={hasUnsavedChanges}
                  formData={formData}
                  disabled={isLoading}
                />
              </div>

              {/* COMMENTED OUT: Step-based navigation and other sections */}
              {/* Step 1: Category & Basic Info */}
              {/* {currentStep === 1 && (
                <div className="space-y-8">
                  <CategorySelector
                    selectedCategory={formData?.category}
                    onCategoryChange={(value) => handleFieldChange('category', value)}
                    error={errors?.category}
                    disabled={isLoading}
                  />
                  
                  {formData?.category && (
                    <MultilingualNameFields
                      formData={formData}
                      onFieldChange={handleFieldChange}
                      errors={errors}
                      disabled={isLoading}
                    />
                  )}
                </div>
              )} */}

              {/* Step 2: Profile & Biography */}
              {/* {currentStep === 2 && (
                <div className="space-y-8">
                  <ProfileImageUpload
                    currentImage={formData?.profileImage}
                    onImageChange={handleImageChange}
                    onImageRemove={handleImageRemove}
                    disabled={isLoading}
                    error={errors?.profileImage}
                  />
                  
                  <BiographyEditor
                    formData={formData}
                    onFieldChange={handleFieldChange}
                    errors={errors}
                    disabled={isLoading}
                  />
                </div>
              )} */}

              {/* Step 3: Contact Information */}
              {/* {currentStep === 3 && (
                <ContactInformation
                  formData={formData}
                  onFieldChange={handleFieldChange}
                  errors={errors}
                  disabled={isLoading}
                />
              )} */}

              {/* Step 4: Position Settings */}
              {/* {currentStep === 4 && (
                <PositionSpecificFields
                  category={formData?.category}
                  formData={formData}
                  onFieldChange={handleFieldChange}
                  errors={errors}
                  disabled={isLoading}
                />
              )} */}

              {/* Step 5: Review & Actions */}
              {/* {currentStep === 5 && (
                <div className="space-y-6">
                  <div className="p-6 bg-card border border-border rounded-lg">
                    <h3 className="text-lg font-semibold text-foreground mb-4">
                      {t('officeForm.positionSummary')}
                    </h3>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-muted-foreground">{t('officeForm.category')}:</span>
                        <span className="ml-2 font-medium">{formData?.category}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">{t('officeForm.nameArabic')}:</span>
                        <span className="ml-2 font-medium">{formData?.name_ar}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">{t('officeForm.nameEnglish')}:</span>
                        <span className="ml-2 font-medium">{formData?.name_en}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">{t('officeForm.contactMethods')}:</span>
                        <span className="ml-2 font-medium">
                          {formData?.contacts?.filter(c => c?.value?.trim())?.length || 0}
                        </span>
                      </div>
                    </div>
                  </div>

                  <FormActions
                    onSave={handleSave}
                    onPreview={handlePreview}
                    onCancel={handleCancel}
                    isLoading={isLoading}
                    hasUnsavedChanges={hasUnsavedChanges}
                    formData={formData}
                    disabled={isLoading}
                  />
                </div>
              )} */}

              {/* Step Navigation */}
              {/* {currentStep < 5 && (
                <div className="flex justify-between pt-6 border-t border-border">
                  <button
                    onClick={prevStep}
                    disabled={currentStep === 1 || isLoading}
                    className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
                      currentStep === 1 || isLoading
                        ? 'text-muted-foreground cursor-not-allowed'
                        : 'text-foreground hover:bg-muted'
                    }`}
                  >
                    <Icon name="ChevronLeft" size={16} />
                    <span>{t('officeForm.previous')}</span>
                  </button>

                  <button
                    onClick={nextStep}
                    disabled={isLoading}
                    className="flex items-center space-x-2 px-6 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50"
                  >
                    <span>{t('officeForm.next')}</span>
                    <Icon name="ChevronRight" size={16} />
                  </button>
                </div>
              )} */}
            </div>

            {/* Sidebar Info - COMMENTED OUT: Simplified form doesn't need step-based sidebar */}
            {/* <div className="space-y-6">
              <div className="p-6 bg-card border border-border rounded-lg">
                <div className="flex items-center space-x-3 mb-4">
                  <Icon 
                    name={formSteps?.[currentStep - 1]?.icon} 
                    size={20} 
                    className="text-primary"
                  />
                  <h3 className="font-semibold text-foreground">
                    {formSteps?.[currentStep - 1]?.title}
                  </h3>
                </div>
                <p className="text-sm text-muted-foreground mb-4">
                  {formSteps?.[currentStep - 1]?.description}
                </p>
                
                <div className="space-y-2 text-xs text-muted-foreground">
                  {currentStep === 1 && (
                    <>
                      <p>{t('officeForm.step1Tip1')}</p>
                      <p>{t('officeForm.step1Tip2')}</p>
                      <p>{t('officeForm.step1Tip3')}</p>
                    </>
                  )}
                  {currentStep === 2 && (
                    <>
                      <p>{t('officeForm.step2Tip1')}</p>
                      <p>{t('officeForm.step2Tip2')}</p>
                      <p>{t('officeForm.step2Tip3')}</p>
                    </>
                  )}
                  {currentStep === 3 && (
                    <>
                      <p>{t('officeForm.step3Tip1')}</p>
                      <p>{t('officeForm.step3Tip2')}</p>
                      <p>{t('officeForm.step3Tip3')}</p>
                    </>
                  )}
                  {currentStep === 4 && (
                    <>
                      <p>{t('officeForm.step4Tip1')}</p>
                      <p>{t('officeForm.step4Tip2')}</p>
                      <p>{t('officeForm.step4Tip3')}</p>
                    </>
                  )}
                  {currentStep === 5 && (
                    <>
                      <p>{t('officeForm.step5Tip1')}</p>
                      <p>{t('officeForm.step5Tip2')}</p>
                      <p>{t('officeForm.step5Tip3')}</p>
                    </>
                  )}
                </div>
              </div>

              <div className="p-6 bg-accent/5 border border-accent/20 rounded-lg">
                <h4 className="font-medium text-foreground mb-3">{t('officeForm.formProgress')}</h4>
                <div className="space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">{t('officeForm.completedSteps')}:</span>
                    <span className="font-medium">{currentStep - 1} / {formSteps?.length}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">{t('officeForm.requiredFields')}:</span>
                    <span className="font-medium">
                      {Object.keys(formData)?.filter(key => 
                        ['name_ar', 'name_en', 'title_ar', 'title_en', 'biography_ar']?.includes(key) && 
                        formData?.[key]?.trim()
                      )?.length} / 5
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">{t('officeForm.languages')}:</span>
                    <span className="font-medium">
                      {['ar', 'en', 'tr']?.filter(lang => 
                        formData?.[`name_${lang}`]?.trim()
                      )?.length} / 3
                    </span>
                  </div>
                </div>
              </div>

              <div className="p-6 bg-muted/30 border border-border rounded-lg">
                <div className="flex items-center space-x-2 mb-3">
                  <Icon name="HelpCircle" size={16} className="text-accent" />
                  <h4 className="font-medium text-foreground">{t('officeForm.needHelp')}</h4>
                </div>
                <p className="text-sm text-muted-foreground mb-3">
                  {t('officeForm.helpDescription')}
                </p>
                <button className="text-sm text-accent hover:underline">
                  {t('officeForm.viewDocumentation')}
                </button>
              </div>
            </div> */}
          </div>
        </div>
      </main>
      <QuickActionButton />
    </div>
  );
};

const OfficeForm = () => {
  return (
    <NotificationProvider>
      <OfficeFormContent />
    </NotificationProvider>
  );
};

export default OfficeForm;